///
/// EarthBrowser
///
/// FrontGlyphGeometry - Extruded triangle (cold front) or semicircle (warm
/// front) marker geometry, built in the XY plane with +Y as the pointing
/// direction and extruded along Z. Shares the "text_lit" shader layout
/// (position/normal/texcoord) so it renders through TextRenderer.
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

export type FrontGlyphShape = 'triangle' | 'semicircle'

const cArcSegments = 10

export class FrontGlyphGeometry implements Geometry {
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public normal: VertexAttribute
	public texcoord: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer

	constructor(
		context: StovokorContext,
		shape: FrontGlyphShape,
		widthKm: number,
		heightKm: number,
		depthKm: number
	) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.texcoord = new VertexAttribute(context, 2)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.texcoord])

		// CCW outline in the XY plane, base on the X axis, pointing +Y
		const outline: { x: number, y: number }[] = []
		const hw = widthKm / 2
		if (shape === 'triangle') {
			outline.push({ x: -hw, y: 0 }, { x: hw, y: 0 }, { x: 0, y: heightKm })
		} else {
			outline.push({ x: -hw, y: 0 }, { x: hw, y: 0 })
			for (let i = 1; i < cArcSegments; i++) {
				const a = (i / cArcSegments) * Math.PI
				outline.push({ x: hw * Math.cos(a), y: heightKm * Math.sin(a) })
			}
		}

		const zoff = depthKm / 2
		let index = 0

		// Front (+Z) and back (-Z) faces as triangle fans of the convex outline
		for (let i = 1; i < outline.length - 1; i++) {
			const tri = [outline[0], outline[i], outline[i + 1]]
			for (const p of tri) {
				this.position.addPos(p.x, p.y, zoff)
				this.normal.add(0, 0, 1)
				this.texcoord.add((p.x + hw) / widthKm, p.y / heightKm)
			}
			this.indexBuffer.triangle(index, index + 1, index + 2)
			index += 3

			for (const p of tri) {
				this.position.addPos(p.x, p.y, -zoff)
				this.normal.add(0, 0, -1)
				this.texcoord.add((p.x + hw) / widthKm, p.y / heightKm)
			}
			this.indexBuffer.triangle(index, index + 2, index + 1)
			index += 3
		}

		// Side panels around the outline
		for (let i = 0; i < outline.length; i++) {
			const a = outline[i]
			const b = outline[(i + 1) % outline.length]
			const dx = b.x - a.x
			const dy = b.y - a.y
			const len = Math.hypot(dx, dy)
			if (len < 1e-6) continue
			// Outward normal for a CCW outline
			const nx = dy / len
			const ny = -dx / len

			this.position.addPos(a.x, a.y, zoff)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(0, 0)
			this.position.addPos(b.x, b.y, zoff)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(1, 0)
			this.position.addPos(a.x, a.y, -zoff)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(0, 1)
			this.position.addPos(b.x, b.y, -zoff)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(1, 1)

			this.indexBuffer.triangle(index, index + 2, index + 1)
			this.indexBuffer.triangle(index + 1, index + 2, index + 3)
			index += 4
		}

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
